/**
 * WSH Unified Filter Bar — Pills + Dropdown Filters + Sort
 *
 * Uses Shopify's Section Rendering API directly for AJAX filter updates.
 * Pills are single-select. Dropdowns (DAW, Mood, Type) are multi-select.
 * All filters compose via AND in the URL.
 *
 * Flow: user action -> buildFilterUrl() -> pushState -> fetch section -> swap DOM
 */
(() => {
  const container = document.querySelector('[data-wsh-quick-filters]');
  if (!container) return;

  const PILL_ACTIVE_CLASS = 'wsh-quick-filters__pill--active';
  const TRIGGER_ACTIVE_CLASS = 'wsh-quick-filters__dropdown-trigger--active';
  const collectionUrl = container.getAttribute('data-collection-url') || window.location.pathname;

  /* =========================================================================
     Dropdown state — tracks selected values for each filter type
     ========================================================================= */
  const activeDropdowns = { daw: [], mood: [] };

  /* AbortController for cancelling in-flight requests */
  let currentAbortController = null;

  /* Filter param mappings */
  const DROPDOWN_PARAMS = {
    daw: 'filter.p.m.custom.daw_filters',
    mood: 'filter.p.m.custom.mood_filters'
  };

  /* =========================================================================
     Core: Section ID + AJAX fetch
     ========================================================================= */
  const getMainSectionId = () => {
    const resultsEl = document.querySelector('results-list[section-id]');
    return resultsEl?.getAttribute('section-id') || null;
  };

  /**
   * Apply a filter URL via AJAX — no page reload.
   */
  const applyFilterURL = async (url) => {
    const sectionId = getMainSectionId();
    if (!sectionId) {
      /* No results-list found — fall back to full page navigation.
         Use window.location.assign() to ensure navigation even if
         the URL matches the current one (pushState edge case). */
      window.location.assign(url);
      return;
    }

    /* Cancel any in-flight request */
    if (currentAbortController) {
      currentAbortController.abort();
    }
    currentAbortController = new AbortController();

    history.pushState({}, '', url);

    const separator = url.includes('?') ? '&' : '?';
    const fetchUrl = `${url}${separator}sections=${sectionId}`;

    try {
      const response = await fetch(fetchUrl, { signal: currentAbortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
      if (sectionEl && data[sectionId]) {
        sectionEl.innerHTML = data[sectionId];
      }

      document.dispatchEvent(new Event('filter:update', { bubbles: true }));

      syncPillStates();
      updateProductCount();
      checkEmptyState();
    } catch (err) {
      if (err.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      currentAbortController = null;
    }
  };

  /* =========================================================================
     URL construction — composes genre pill + type pill + dropdown + sort params
     Two independent axes: genre (ALL + genre/subgenre pills) and type (DRUMS, BUNDLES).
     ========================================================================= */
  const buildFilterUrl = () => {
    const url = new URL(collectionUrl, window.location.origin);

    /* Genre axis — single active genre pill (not ALL) */
    const activeGenrePill = container.querySelector(
      `[data-wsh-pill][data-wsh-pill-axis="genre"]:not([data-wsh-pill-all]).${PILL_ACTIVE_CLASS}`
    );
    if (activeGenrePill) {
      const param = activeGenrePill.getAttribute('data-wsh-pill-param');
      const value = activeGenrePill.getAttribute('data-wsh-pill-value');
      if (param && value) url.searchParams.set(param, value);
    }

    /* Type axis — single active type pill */
    const activeTypePill = container.querySelector(
      `[data-wsh-pill][data-wsh-pill-axis="type"].${PILL_ACTIVE_CLASS}`
    );
    if (activeTypePill) {
      const param = activeTypePill.getAttribute('data-wsh-pill-param');
      const value = activeTypePill.getAttribute('data-wsh-pill-value');
      if (param && value) url.searchParams.set(param, value);
    }

    /* DAW filters (multi-select) */
    activeDropdowns.daw.forEach((handle) => {
      url.searchParams.append(DROPDOWN_PARAMS.daw, handle);
    });

    /* Mood filters (multi-select via tags) */
    activeDropdowns.mood.forEach((mood) => {
      url.searchParams.append(DROPDOWN_PARAMS.mood, mood);
    });

    /* Preserve sort_by */
    const currentParams = new URLSearchParams(window.location.search);
    const sortBy = currentParams.get('sort_by');
    if (sortBy) url.searchParams.set('sort_by', sortBy);

    return url.pathname + url.search;
  };

  /* =========================================================================
     Pill click handler — two independent axes (genre + type)
     ========================================================================= */
  container.addEventListener('click', (e) => {
    const pill = e.target.closest('[data-wsh-pill]');
    if (!pill) return;
    e.preventDefault();

    const isAllPill = pill.hasAttribute('data-wsh-pill-all');
    const axis = pill.getAttribute('data-wsh-pill-axis');

    if (isAllPill) {
      /* ALL clears genre axis only — type axis stays as-is */
      container.querySelectorAll('[data-wsh-pill][data-wsh-pill-axis="genre"]').forEach((p) => {
        p.classList.remove(PILL_ACTIVE_CLASS);
      });
      pill.classList.add(PILL_ACTIVE_CLASS);
    } else if (axis === 'type') {
      /* Type axis: mutually exclusive, toggle-off on re-click */
      const wasActive = pill.classList.contains(PILL_ACTIVE_CLASS);
      container.querySelectorAll('[data-wsh-pill][data-wsh-pill-axis="type"]').forEach((p) => {
        p.classList.remove(PILL_ACTIVE_CLASS);
      });
      if (!wasActive) {
        pill.classList.add(PILL_ACTIVE_CLASS);
      }
    } else if (axis === 'genre') {
      /* Genre axis: mutually exclusive with ALL and other genre pills */
      container.querySelectorAll('[data-wsh-pill][data-wsh-pill-axis="genre"]').forEach((p) => {
        p.classList.remove(PILL_ACTIVE_CLASS);
      });
      pill.classList.add(PILL_ACTIVE_CLASS);
    }

    const url = buildFilterUrl();
    applyFilterURL(url);
  });

  /* =========================================================================
     Sort select handler
     ========================================================================= */
  const sortSelect = container.querySelector('[data-wsh-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      applyFilterURL(url.pathname + url.search);
    });
  }

  /* =========================================================================
     Dropdown open/close logic
     ========================================================================= */
  let openPopoverType = null;

  const isMobile = () => window.innerWidth < 750;

  const closeAllPopovers = () => {
    container.querySelectorAll('[data-wsh-popover]').forEach((p) => {
      p.style.display = 'none';
    });
    container.querySelectorAll('[data-wsh-dropdown-trigger]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
    });
    openPopoverType = null;
  };

  const openPopover = (filterType) => {
    const trigger = container.querySelector(`[data-wsh-dropdown-trigger="${filterType}"]`);
    const popover = container.querySelector(`[data-wsh-popover="${filterType}"]`);
    if (!trigger || !popover) return;

    /* Position popover below trigger */
    const triggerRect = trigger.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    popover.style.left = `${triggerRect.left - containerRect.left}px`;
    popover.style.top = `${triggerRect.bottom - containerRect.top + 4}px`;

    popover.style.display = 'block';
    trigger.setAttribute('aria-expanded', 'true');
    openPopoverType = filterType;
  };

  const openSheet = (filterType) => {
    const sheet = container.querySelector(`[data-wsh-sheet="${filterType}"]`);
    const overlay = container.querySelector('[data-wsh-sheet-overlay]');
    if (!sheet || !overlay) return;

    /* Sync sheet checkboxes with popover state */
    syncSheetCheckboxes(filterType);

    overlay.style.display = 'block';
    sheet.style.display = 'block';
    requestAnimationFrame(() => {
      overlay.classList.add('wsh-quick-filters__sheet-overlay--visible');
      sheet.classList.add('wsh-quick-filters__sheet--visible');
    });
    document.body.style.overflow = 'hidden';
  };

  const closeSheet = () => {
    const overlay = container.querySelector('[data-wsh-sheet-overlay]');
    const sheets = container.querySelectorAll('[data-wsh-sheet]');

    overlay?.classList.remove('wsh-quick-filters__sheet-overlay--visible');
    sheets.forEach((s) => s.classList.remove('wsh-quick-filters__sheet--visible'));

    setTimeout(() => {
      if (overlay) overlay.style.display = 'none';
      sheets.forEach((s) => { s.style.display = 'none'; });
      document.body.style.overflow = '';
    }, 200);
  };

  const syncSheetCheckboxes = (filterType) => {
    const selected = activeDropdowns[filterType] || [];
    const sheet = container.querySelector(`[data-wsh-sheet="${filterType}"]`);
    if (!sheet) return;
    sheet.querySelectorAll(`[data-wsh-dropdown-checkbox="${filterType}"]`).forEach((cb) => {
      cb.checked = selected.includes(cb.getAttribute('data-wsh-filter-value'));
    });
  };

  /* Trigger click handler */
  container.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-wsh-dropdown-trigger]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();

    const filterType = trigger.getAttribute('data-wsh-dropdown-trigger');

    if (isMobile()) {
      openSheet(filterType);
      return;
    }

    if (openPopoverType === filterType) {
      closeAllPopovers();
    } else {
      closeAllPopovers();
      openPopover(filterType);
    }
  });

  /* Click outside to close popover */
  document.addEventListener('click', (e) => {
    if (!openPopoverType) return;
    const popover = container.querySelector(`[data-wsh-popover="${openPopoverType}"]`);
    const trigger = container.querySelector(`[data-wsh-dropdown-trigger="${openPopoverType}"]`);
    if (popover && !popover.contains(e.target) && trigger && !trigger.contains(e.target)) {
      closeAllPopovers();
    }
  });

  /* Escape to close */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (openPopoverType) closeAllPopovers();
      closeSheet();
    }
  });

  /* Sheet overlay click to close */
  container.querySelector('[data-wsh-sheet-overlay]')?.addEventListener('click', closeSheet);

  /* Sheet "Done" buttons */
  container.querySelectorAll('[data-wsh-sheet-done]').forEach((btn) => {
    btn.addEventListener('click', closeSheet);
  });

  /* =========================================================================
     Checkbox change handler — dropdown filter selection
     ========================================================================= */
  const onDropdownChange = (filterType, value, isChecked) => {
    if (isChecked) {
      if (!activeDropdowns[filterType].includes(value)) {
        activeDropdowns[filterType].push(value);
      }
    } else {
      activeDropdowns[filterType] = activeDropdowns[filterType].filter((v) => v !== value);
    }

    updateTriggerLabel(filterType);
    renderActiveChips();
    syncAllCheckboxes(filterType);

    const url = buildFilterUrl();
    applyFilterURL(url);
  };

  /* Listen to checkbox changes in both popovers and sheets */
  container.addEventListener('change', (e) => {
    const checkbox = e.target.closest('[data-wsh-dropdown-checkbox]');
    if (!checkbox) return;

    const filterType = checkbox.getAttribute('data-wsh-dropdown-checkbox');
    const value = checkbox.getAttribute('data-wsh-filter-value');
    onDropdownChange(filterType, value, checkbox.checked);
  });

  /* =========================================================================
     Trigger label update
     ========================================================================= */
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const getDisplayName = (filterType, value) => {
    if (filterType === 'mood') {
      return capitalize(value);
    }
    /* DAW: find the display name from checkbox data attributes */
    const checkbox = container.querySelector(
      `[data-wsh-popover="${filterType}"] [data-wsh-filter-value="${value}"]`
    );
    return checkbox?.getAttribute('data-wsh-filter-display') || value;
  };

  const updateTriggerLabel = (filterType) => {
    const label = container.querySelector(`[data-wsh-dropdown-label="${filterType}"]`);
    const trigger = container.querySelector(`[data-wsh-dropdown-trigger="${filterType}"]`);
    if (!label || !trigger) return;

    const selected = activeDropdowns[filterType];
    const baseName = capitalize(filterType);

    if (selected.length === 0) {
      label.textContent = baseName;
      trigger.classList.remove(TRIGGER_ACTIVE_CLASS);
    } else if (selected.length === 1) {
      label.textContent = `${baseName}: ${getDisplayName(filterType, selected[0])}`;
      trigger.classList.add(TRIGGER_ACTIVE_CLASS);
    } else {
      label.textContent = `${baseName} (${selected.length})`;
      trigger.classList.add(TRIGGER_ACTIVE_CLASS);
    }
  };

  /* =========================================================================
     Active chip rendering
     ========================================================================= */
  const renderActiveChips = () => {
    const chipsContainer = container.querySelector('[data-wsh-filter-chips]');
    const chipsList = container.querySelector('[data-wsh-chips-list]');
    if (!chipsContainer || !chipsList) return;

    chipsList.innerHTML = '';
    let hasChips = false;

    ['daw', 'mood'].forEach((filterType) => {
      activeDropdowns[filterType].forEach((value) => {
        hasChips = true;
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'wsh-quick-filters__chip';
        chip.setAttribute('data-wsh-chip-type', filterType);
        chip.setAttribute('data-wsh-chip-value', value);

        const displayName = getDisplayName(filterType, value);
        chip.innerHTML = `<span>${displayName}</span><span class="wsh-quick-filters__chip-dismiss">&times;</span>`;
        chipsList.appendChild(chip);
      });
    });

    chipsContainer.style.display = hasChips ? 'block' : 'none';
  };

  /* Chip dismiss handler */
  container.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-wsh-chip-type]');
    if (!chip) return;
    e.preventDefault();

    const filterType = chip.getAttribute('data-wsh-chip-type');
    const value = chip.getAttribute('data-wsh-chip-value');
    onDropdownChange(filterType, value, false);
  });

  /* "Clear all" chips handler */
  container.querySelector('[data-wsh-chips-clear]')?.addEventListener('click', (e) => {
    e.preventDefault();
    activeDropdowns.daw = [];
    activeDropdowns.mood = [];

    ['daw', 'mood'].forEach((ft) => {
      updateTriggerLabel(ft);
      syncAllCheckboxes(ft);
    });
    renderActiveChips();

    const url = buildFilterUrl();
    applyFilterURL(url);
  });

  /* Empty state "Clear filters" handler */
  container.querySelector('[data-wsh-empty-clear]')?.addEventListener('click', (e) => {
    e.preventDefault();
    activeDropdowns.daw = [];
    activeDropdowns.mood = [];

    /* Reset pills to "All" */
    const pills = container.querySelectorAll('[data-wsh-pill]');
    pills.forEach((p) => p.classList.remove(PILL_ACTIVE_CLASS));
    const allPill = container.querySelector('[data-wsh-pill-all]');
    if (allPill) allPill.classList.add(PILL_ACTIVE_CLASS);

    ['daw', 'mood'].forEach((ft) => {
      updateTriggerLabel(ft);
      syncAllCheckboxes(ft);
    });
    renderActiveChips();

    applyFilterURL(collectionUrl);
  });

  /* =========================================================================
     Sync checkbox states across popover and sheet for a filter type
     ========================================================================= */
  const syncAllCheckboxes = (filterType) => {
    const selected = activeDropdowns[filterType];
    container.querySelectorAll(`[data-wsh-dropdown-checkbox="${filterType}"]`).forEach((cb) => {
      cb.checked = selected.includes(cb.getAttribute('data-wsh-filter-value'));
    });
  };

  /* =========================================================================
     Sync dropdown states from URL (on load + popstate)
     ========================================================================= */
  const syncDropdownStates = () => {
    const params = new URLSearchParams(window.location.search);

    /* DAW */
    activeDropdowns.daw = params.getAll(DROPDOWN_PARAMS.daw);

    /* Mood */
    activeDropdowns.mood = params.getAll(DROPDOWN_PARAMS.mood);

    /* Update UI */
    ['daw', 'mood'].forEach((ft) => {
      updateTriggerLabel(ft);
      syncAllCheckboxes(ft);
    });
    renderActiveChips();
  };

  /* =========================================================================
     Sync pill active states from URL — independent per axis
     Genre axis: ALL is active when no genre/subgenre param matches.
     Type axis: DRUMS/BUNDLES active when their param matches.
     Idempotent — safe to call multiple times in succession.
     ========================================================================= */
  const syncPillStates = () => {
    const params = new URLSearchParams(window.location.search);
    const pills = container.querySelectorAll('[data-wsh-pill]');

    /* Check if any genre pill matches the URL */
    let hasGenreFilter = false;
    pills.forEach((pill) => {
      if (pill.hasAttribute('data-wsh-pill-all')) return;
      if (pill.getAttribute('data-wsh-pill-axis') !== 'genre') return;
      const pp = pill.getAttribute('data-wsh-pill-param');
      const pv = pill.getAttribute('data-wsh-pill-value');
      if (pp && pv && params.getAll(pp).includes(pv)) {
        hasGenreFilter = true;
      }
    });

    /* Set active state per pill based on axis */
    pills.forEach((pill) => {
      if (pill.hasAttribute('data-wsh-pill-all')) {
        pill.classList.toggle(PILL_ACTIVE_CLASS, !hasGenreFilter);
        return;
      }
      const pillParam = pill.getAttribute('data-wsh-pill-param');
      const pillValue = pill.getAttribute('data-wsh-pill-value');
      if (!pillParam || !pillValue) return;
      pill.classList.toggle(PILL_ACTIVE_CLASS, params.getAll(pillParam).includes(pillValue));
    });
  };

  /* =========================================================================
     Product count update
     ========================================================================= */
  const updateProductCount = () => {
    const countEl = container.querySelector('[data-wsh-product-count]');
    if (!countEl) return;
    setTimeout(() => {
      const nativeCount = document.querySelector('[data-testid="products-count"] span');
      if (nativeCount) countEl.textContent = nativeCount.textContent.trim();
    }, 300);
  };

  /* =========================================================================
     Empty state detection
     ========================================================================= */
  const checkEmptyState = () => {
    const emptyEl = container.querySelector('[data-wsh-empty-state]');
    if (!emptyEl) return;
    setTimeout(() => {
      const productCards = document.querySelectorAll(
        '.product-grid .product-card, .product-grid .grid__item, .collection-product-list .product-card, [data-product-grid] .grid__item, .main-collection .grid__item'
      );
      emptyEl.style.display = productCards.length === 0 ? 'block' : 'none';
    }, 350);
  };

  /* =========================================================================
     Filter-active visibility toggle
     When any filter is active, hide featured/curated sections and show
     main-collection as the primary product display.
     ========================================================================= */
  const FILTERS_ACTIVE_CLASS = 'wsh-filters-active';

  const updateFilterActiveState = () => {
    const params = new URLSearchParams(window.location.search);
    const hasFilters = [...params.keys()].some((k) => k.startsWith('filter.'));
    document.body.classList.toggle(FILTERS_ACTIVE_CLASS, hasFilters);
  };

  /* Run on every filter change and page load */
  document.addEventListener('filter:update', updateFilterActiveState);
  window.addEventListener('popstate', updateFilterActiveState);
  updateFilterActiveState(); /* init on page load */

  /* =========================================================================
     Event listeners
     ========================================================================= */
  document.addEventListener('filter:update', () => {
    requestAnimationFrame(syncPillStates);
    syncDropdownStates();
  });

  window.addEventListener('popstate', () => {
    syncPillStates();
    syncDropdownStates();
  });

  document.addEventListener('filter:update', updateProductCount);

  /* =========================================================================
     Init — sync state from URL on page load
     ========================================================================= */
  syncDropdownStates();
})();
